const PROGRESS_STATUS = {
  COMPLETED: "completed",
  COMPLETE: "completed",
  SUCCEEDED: "completed",
  SKIPPED: "completed",

  WAITING: "pending",
  PENDING: "pending",
  QUEUED: "pending",
  DISPATCHING: "pending",
  RUNNING: "pending",
  RETRYING: "pending",
  RECOVERING: "pending",

  FAILED: "failed",
  ERROR: "failed",
  TIMEOUT: "failed",
  "TIMED OUT": "failed",
  "TIMED-OUT": "failed",
  INTERRUPTED: "failed",
  BLOCKED: "failed"
};

export function buildWorkflowProgress(workflow, snapshot) {
  if (!snapshot?.inventoryComplete || !snapshot.sheets?.length) return null;
  // The source inventory defines obligations, including work not yet submitted.
  const statuses = snapshot.sheets.flatMap(sheet => SHEET_OPERATIONS.map(key => sheet[key]));
  statuses.push(snapshot.consolidation);
  const counts = { completed: 0, pending: 0, failed: 0 };
  for (const value of statuses) {
    const category = progressCategory(value?.code);
    if (category) counts[category]++;
  }
  const classified = counts.completed + counts.pending + counts.failed;
  return { counts, total: statuses.length, classified, unknown: statuses.length - classified,
    percentage: Math.floor(counts.completed * 100 / statuses.length), basis: "required-operations",
    workflowStatus: workflow?.status || null, workflowId: workflow?.workflowId || null };
}

const SHEET_OPERATIONS = ["transform", "macros", "copy"];
const HISTORY_OPERATIONS = new Set([...SHEET_OPERATIONS, "consolidation"]);
const OPERATION_LABELS = { mapping: "Header review", transform: "Transform", macros: "Run macros",
  copy: "Copy unmapped columns", consolidation: "Combine sheets", processing: "Processing" };
const completedCodes = new Set(["output", "recorded", "skipped", "completed", "copied"]);
const failedCodes = new Set(["failed", "interrupted", "blocked"]);
function progressCategory(code) {
  if (completedCodes.has(code)) return "completed";
  if (failedCodes.has(code)) return "failed";
  if (["pending", "running", "partial", "stale"].includes(code)) return "pending";
  return null;
}

// Projection and request construction only; host bridges own execution.
const normalizedWorkflowStatus = status => typeof status === "string" ? status.trim().toUpperCase() : "";
export const sovWorkflowTerminal = status => ["COMPLETED", "FAILED", "PARTIAL_FAILED", "CANCELLED", "CANCELED", "INTERRUPTED"].includes(normalizedWorkflowStatus(status));
export function getSovWorkflowActivity(workflow, metadata, now = Date.now()) {
  const lock = metadata?.activeSovWorkflow;
  // The document lease is the backend's authority for accepting another workflow.
  const active = Boolean(lock?.workflowId && lock.ownerId && Date.parse(lock.leaseExpiresAt) > now);
  const savedStatus = normalizedWorkflowStatus(workflow?.status);
  const sameWorkflow = lock?.workflowId === workflow?.workflowId && lock?.generationId === workflow?.generationId;
  const terminal = sovWorkflowTerminal(savedStatus);
  return { active, stale: Boolean(workflow && !active && !terminal),
    status: active ? (sameWorkflow && ["QUEUED", "RUNNING", "RECOVERING", "RETRYING"].includes(savedStatus) ? savedStatus : "RUNNING")
      : terminal ? savedStatus : workflow ? "NOT_RUNNING" : null };
}
export function getSovWorkflowPreparation(workflow, metadata, now = Date.now()) {
  const preparation = workflow?.preparation;
  const status = normalizedWorkflowStatus(preparation?.status);
  if (status === "FAILED") return { active: false, title: "Preparation failed",
    message: typeof preparation.lastError === "string" && preparation.lastError
      ? preparation.lastError : "The selected sheets could not be prepared. Review the error and run the workflow again." };
  const lock = metadata?.activeSovWorkflow;
  if (!["PENDING", "RUNNING"].includes(status) || sovWorkflowTerminal(workflow?.status)
    || !getSovWorkflowActivity(workflow, metadata, now).active
    || lock.workflowId !== workflow.workflowId || lock.generationId !== workflow.generationId) return null;
  return { active: true, title: "Preparing workflow", message: status === "RUNNING"
    ? "Checking source sheets and preparing transformations. Operations will appear when preparation finishes."
    : Number(preparation.attempts) > 0
      ? "Preparation is queued for another attempt. The workflow will resume automatically."
      : "The workflow is queued to prepare the selected sheets and saved mappings." };
}
export function getSovWorkflowSelection(workflow, submittedRequest = null) {
  const preparation = workflow?.preparation;
  const operations = Array.isArray(workflow?.operations) ? workflow.operations : [];
  const savedRequest = ["PENDING", "RUNNING"].includes(normalizedWorkflowStatus(preparation?.status))
    && Array.isArray(preparation?.request?.sheetActions) ? preparation.request : null;
  const request = savedRequest || (!operations.length && !sovWorkflowTerminal(workflow?.status)
    && submittedRequest?.workflowId && submittedRequest.workflowId === workflow?.workflowId ? submittedRequest : null);
  const selections = (Array.isArray(request?.sheetActions) ? request.sheetActions : null) || operations
    .map(operation => ({ inputSheetName: operation.inputSheetName, actions: [operation.action] }));
  const workbookActions = workflow?.workbookActions || request?.workbookActions;
  const combineNames = Array.isArray(workbookActions?.combineSheetNames)
    ? workbookActions.combineSheetNames
    : (Array.isArray(workflow?.combineSources) ? workflow.combineSources : []).map(source => source.inputSheetName);
  return { sheetNames: [...new Set([...selections.map(sheet => sheet.inputSheetName), ...combineNames]
    .filter(name => typeof name === "string" && name.trim() && name !== "Combined Sheet"))],
    actions: [...new Set(selections.flatMap(sheet => Array.isArray(sheet.actions) ? sheet.actions : [])
      .filter(action => ["transform", "copy_unmapped_columns", "runmacros"].includes(action)))] };
}
export async function loadSovWorkflowHistory(latest, load, isCancelled = () => false) {
  const workflows = [];
  const seen = new Set([latest.workflowId]);
  let id = latest.previousWorkflowId;
  while (id && workflows.length < 20 && !isCancelled()) {
    if (typeof id !== "string" || !/^[A-Za-z0-9_-]{1,100}$/.test(id) || seen.has(id)) {
      return { workflows, warning: "History stopped at an invalid or cyclic workflow link." };
    }
    seen.add(id);
    let workflow;
    try { workflow = (await load(id))?.workflow; }
    catch { return { workflows, warning: "An earlier workflow is missing or could not be read. Previously loaded history is shown." }; }
    if (!workflow || workflow.workflowId !== id) {
      return { workflows, warning: "History stopped at an unavailable or mismatched workflow record." };
    }
    if (isCancelled()) break;
    workflows.push(workflow);
    id = workflow.previousWorkflowId;
  }
  return { workflows, warning: id && workflows.length === 20 ? "Showing the most recent 20 previous workflows." : "" };
}
export function buildSovWorkflowRequest(metadata, selections, combineSheets, workflowId, platform = false, options = {}) {
  const names = new Set();
  const sheetActions = [];
  const selectedNames = options.selectedSheetNames;
  if (selectedNames !== undefined && (!Array.isArray(selectedNames) || !selectedNames.length
    || selectedNames.some(name => typeof name !== "string" || !name.trim())
    || new Set(selectedNames).size !== selectedNames.length)) throw new Error("Select at least one valid source sheet.");
  for (const sheet of metadata?.sheetData || []) {
    const name = sheet.inputSheetName;
    if (!name || name === "Combined Sheet") continue;
    const normalized = name.trim().toLowerCase();
    if (names.has(normalized)) throw new Error("Ambiguous sheet names. Resolve them in the extraction tool.");
    names.add(normalized);
    if (selectedNames && !selectedNames.includes(name)) continue;
    const actions = ["transform", "copy_unmapped_columns", "runmacros"].filter(action => selections[name]?.includes(action));
    if (actions.length) sheetActions.push({ inputSheetName: name, actions });
  }
  if (selectedNames?.some(name => !(metadata?.sheetData || []).some(sheet => sheet.inputSheetName === name && name !== "Combined Sheet"))) {
    throw new Error("A selected source sheet is unavailable. Refresh the sheet list.");
  }
  if (combineSheets) {
    const eligibleCount = countCombineEligibleSheets(metadata, selections, selectedNames);
    if (eligibleCount < 2) throw new Error("Combine Sheets requires at least two eligible source sheets.");
    if (selectedNames && eligibleCount !== selectedNames.length) {
      throw new Error("Every selected combine source needs a saved output or a selected processing action.");
    }
  }
  if (options.safeParallel !== undefined && typeof options.safeParallel !== "boolean") throw new Error("Invalid workflow execution mode.");
  if (!sheetActions.length && !combineSheets) throw new Error("Select an action or Combine Sheets.");
  const schemaVersion = selectedNames !== undefined || options.safeParallel !== undefined
    || sheetActions.some(sheet => sheet.actions.includes("transform")) ? 2 : 1;
  return { target: "start-sov-workflow", schemaVersion, workflowId, sheetActions,
    workbookActions: { combineSheets: combineSheets === true,
      ...(combineSheets && selectedNames ? { combineSheetNames: [...selectedNames] } : {}) }, platform: platform === true,
    ...(options.safeParallel !== undefined ? { safeParallel: options.safeParallel } : {}) };
}
export function countCombineEligibleSheets(metadata, selections = {}, selectedNames) {
  return (metadata?.sheetData || []).filter(sheet => sheet?.inputSheetName
    && sheet.inputSheetName !== "Combined Sheet"
    && (!selectedNames || selectedNames.includes(sheet.inputSheetName))
    && (Boolean(sheet.transformationFilePath) || ["transform", "copy_unmapped_columns", "runmacros"]
      .some(action => selections[sheet.inputSheetName]?.includes(action)))).length;
}
export function isSovAnalyseEnabled(templateRules, instanceConfig) {
  const configured = templateRules?.metaData?.sovAnalyseWorkspace?.enabled;
  return configured === undefined ? instanceConfig?.enabled === true : configured === true;
}

export async function loadSovAnalyseMetadata(document, readObject) {
  const folder = document?.documentFullPath?.replace(/^public\//, "").split("/")[0];
  const id = document?.document_id;
  if (!folder || !id || /[/\\]/.test(id) || folder === "." || folder === "..") {
    throw new Error("Document metadata location is unavailable.");
  }
  const read = async (key, maxBytes) => {
    let blob;
    try {
      blob = await readObject(key);
    } catch (error) {
      const code = error?.name || error?.code;
      if (["NoSuchKey", "NotFound"].includes(code) || error?.$metadata?.httpStatusCode === 404) return null;
      throw new Error("Unable to refresh processing status. Check access and try again.");
    }
    if (!blob || typeof blob.text !== "function" || !Number.isFinite(blob.size)) {
      throw new Error("Processing metadata could not be read.");
    }
    if (blob.size > maxBytes) throw new Error("Processing metadata exceeds the dashboard size limit.");
    let value;
    try { value = JSON.parse(await blob.text()); } catch {
      throw new Error("Saved processing metadata is not valid JSON.");
    }
    if (!value || typeof value !== "object" || Array.isArray(value) || value.format === "mea.extractedWorkbookManifest") {
      throw new Error("Saved processing metadata has an unsupported format.");
    }
    return value;
  };
  const [metadata, apiStatus] = await Promise.all([
    read(`${folder}/DocumentsMeta/${id}.json`, 5 * 1024 * 1024),
    read(`${folder}/api_status.json`, 2 * 1024 * 1024)
  ]);
  return { metadata: metadata || {}, apiStatus: apiStatus || {}, fetchedAt: new Date().toISOString(),
    metadataMissing: metadata === null, statusMissing: apiStatus === null };
}

const labels = {
  unknown: "Unknown", skipped: "Completed", saved: "Completed", output: "Completed",
  recorded: "Completed", completed: "Completed", copied: "Copied", partial: "Partial",
  included: "Included", excluded: "Not included", running: "In progress",
  pending: "Pending", failed: "Failed", blocked: "Blocked", interrupted: "Failed", stale: "Pending"
};
function state(code, detail = "") { return { code, label: labels[code], detail }; }
function normalizedStatus(value) {
  const status = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (["running", "retrying", "recovering", "dispatching"].includes(status)) return "running";
  if (["pending", "queued", "waiting"].includes(status)) return "pending";
  if (status === "blocked") return "blocked";
  if (["failed", "error"].includes(status)) return "failed";
  if (["timed out", "timeout", "timed-out", "interrupted"].includes(status)) return "interrupted";
  if (["complete", "completed", "succeeded", "skipped"].includes(status)) return "recorded";
  return "unknown";
}
function statusTime(record) {
  return latestTimestamp([record?.progress?.updatedAt, record?.lastTimeStamp, record?.updatedAt]);
}
function latestTimestamp(values) {
  return values.filter(value => typeof value === "string" && Number.isFinite(Date.parse(value)))
    .sort((left, right) => Date.parse(right) - Date.parse(left))[0] || null;
}
function workflowOperationTime(operation, workflow) {
  const attempt = operation?.attempts?.at(-1);
  return latestTimestamp([attempt?.finishedAt, attempt?.updatedAt, attempt?.dispatchedAt, attempt?.startedAt,
    operation?.updatedAt, operation?.finishedAt, operation?.startedAt])
    || latestTimestamp([workflow?.updatedAt, workflow?.finishedAt, workflow?.createdAt]);
}
function latestOperationEvidence(previous, next) {
  if (!previous) return next;
  if (previous.saved !== next.saved && (previous.saved || next.saved)) return previous.saved ? next : previous;
  const previousTime = Date.parse(previous.at) || 0;
  const nextTime = Date.parse(next.at) || 0;
  if (previousTime && nextTime && previousTime !== nextTime) return nextTime > previousTime ? next : previous;
  if (Boolean(previous.workflow) !== Boolean(next.workflow)) {
    const workflow = previous.workflow ? previous : next;
    const api = previous.workflow ? next : previous;
    if (api.at && !workflow.at && progressCategory(api.code) !== progressCategory(workflow.code)) {
      return { ...workflow, code: "unknown", label: labels.unknown, at: null,
        detail: "API and workflow statuses disagree and their order cannot be verified. Refresh and review the operation." };
    }
    // An explicit workflow supersedes legacy API entries that have no timestamp.
    return workflow;
  }
  return nextTime >= previousTime ? next : previous;
}
function mappingsOf(sheet) {
  return Array.isArray(sheet?.qaModKeyValues) ? sheet.qaModKeyValues : Array.isArray(sheet?.keyValues) ? sheet.keyValues : [];
}
function operationOf(record) {
  const action = String(record?.progress?.mappedDataAction || record?.mappedDataAction || "").toLowerCase();
  if (action === "runmacros" || record?.progress?.isRunMacro === true || record?.isRunMacro === true) return "macros";
  if (["copy_unmapped_columns", "remove_unmapped_columns"].includes(action)) return "copy";
  if (action === "transform") return "transform";
  return null; // Legacy latest status cannot be attributed to an action by guessing.
}

function workflowOperationTarget(operation) {
  const action = String(operation.action || operation.type || operation.operationType || "").trim().toLowerCase();
  return ({ runmacros: "macros", copy_unmapped_columns: "copy", transform: "transform",
    transform_sheets: "transform", "combine-sheets": "consolidation" })[action] || null;
}
function isUnattemptedWorkflowPlan(operation) {
  return ["WAITING", "PENDING", "QUEUED"].includes(String(operation.status || "").toUpperCase())
    && Array.isArray(operation.attempts) && operation.attempts.length === 0
    && !operation.startedAt && !operation.dispatchedAt && !operation.finishedAt && !operation.result;
}
function latestWorkflowOperations(workflow) {
  const latest = new Map();
  for (const operation of Array.isArray(workflow?.operations) ? workflow.operations : []) {
    if (!operation || typeof operation !== "object") continue;
    const target = workflowOperationTarget(operation);
    if (!target) continue;
    const key = `${String(operation.inputSheetName || "").trim().toLowerCase()}:${target}`;
    const previous = latest.get(key);
    if (!previous || (Date.parse(workflowOperationTime(operation, workflow)) || 0)
      >= (Date.parse(workflowOperationTime(previous, workflow)) || 0)) latest.set(key, operation);
  }
  return [...latest.values()];
}

export function groupSovAnalyseHistory(attempts) {
  const latest = new Map();
  for (const attempt of attempts) {
    const key = `${attempt.operation}:${attempt.sheetId || attempt.sheet}`;
    latest.set(key, latestOperationEvidence(latest.get(key), attempt));
  }
  const groups = new Map();
  const rank = code => failedCodes.has(code) ? 4 : ["running", "pending"].includes(code) ? 3
    : code === "unknown" || code === "partial" ? 2 : 1;
  for (const attempt of latest.values()) {
    const group = groups.get(attempt.operation) || { id: attempt.operation, operation: attempt.operation,
      operationLabel: OPERATION_LABELS[attempt.operation], sheets: [], sheetIds: [], entries: [], code: attempt.code, at: null };
    for (const name of attempt.sheets || [attempt.sheet]) if (name && !group.sheets.includes(name)) group.sheets.push(name);
    if (attempt.sheetId && !group.sheetIds.includes(attempt.sheetId)) group.sheetIds.push(attempt.sheetId);
    group.entries.push(attempt);
    if (rank(attempt.code) > rank(group.code)) group.code = attempt.code;
    if ((Date.parse(attempt.at) || 0) > (Date.parse(group.at) || 0)) group.at = attempt.at;
    groups.set(attempt.operation, group);
  }
  return [...groups.values()].map(group => ({ ...group, sheet: group.sheets.join(", "),
    label: labels[group.code] || "Unknown", detail: group.entries.some(entry => entry.saved)
      ? "Saved status and latest recorded attempts. Saved statuses have no recorded execution time; failures take precedence."
      : "Latest recorded status for each sheet; failures take precedence in this group." }))
    .sort((left, right) => (Date.parse(right.at) || 0) - (Date.parse(left.at) || 0));
}

export function groupSovReadinessItems(items = [], sheets = []) {
  const sheetById = new Map(sheets.map(sheet => [sheet.id, sheet]));
  const groups = new Map();
  for (const item of items) {
    const operation = item.operation || item.id;
    const group = groups.get(operation) || { operation, title: OPERATION_LABELS[operation] || item.title, sheets: [], details: [] };
    const sheet = sheetById.get(item.sheetId);
    if (sheet && !group.sheets.some(existing => existing.name === sheet.name)) group.sheets.push({ id: sheet.id, name: sheet.name });
    if (!sheet && item.title !== group.title && !group.details.includes(item.title)) group.details.push(item.title);
    if (item.detail && !group.details.includes(item.detail)) group.details.push(item.detail);
    groups.set(operation, group);
  }
  return [...groups.values()];
}

function buildReadiness(sheets, consolidation, inventoryComplete, history) {
  const blockers = [], pendingSteps = [];
  if (!inventoryComplete) blockers.push({ id: "inventory", title: "Verify source sheet inventory",
    detail: "The complete source sheet inventory is unverified.", operation: "mapping" });
  const inspect = (value, operation, sheet) => {
    if (completedCodes.has(value.code) || (operation === "mapping" && value.code === "saved")) return;
    const entry = { id: `${operation}:${sheet?.name || "workbook"}`, sheetId: sheet?.id, operation,
      title: `${OPERATION_LABELS[operation]}${sheet ? ` — ${sheet.name}` : ""}` };
    if (value.code !== "pending") entry.detail = value.detail || value.label;
    if (operation === "mapping" || failedCodes.has(value.code) || value.code === "unknown") blockers.push(entry);
    else pendingSteps.push(entry);
  };
  for (const sheet of sheets) {
    inspect(sheet.mapping, "mapping", sheet);
    for (const key of SHEET_OPERATIONS) inspect(sheet[key], key, sheet);
  }
  if (sheets.length) inspect(consolidation, "consolidation");
  for (const attempt of history.find(group => group.operation === "processing")?.entries || []) {
    if (!completedCodes.has(attempt.code)) inspect(state(attempt.code, attempt.detail), "processing",
      sheets.find(sheet => sheet.id === attempt.sheetId));
  }
  return { blockers, pendingSteps, manualBlockers: blockers.length,
    pendingAutomaticSteps: pendingSteps.length, verified: false };
}

function recommendAction(sheets, consolidation, active, inventoryComplete) {
  const candidate = (sheet, operation) => ({ sheet, operation });
  let next;
  for (const sheet of sheets) {
    const failure = SHEET_OPERATIONS.find(key => failedCodes.has(sheet[key].code));
    if (failure) { next = candidate(sheet, failure === "transform" && sheet.mapping.code !== "saved" ? "mapping" : failure); break; }
  }
  if (!next && failedCodes.has(consolidation.code)) next = candidate(null, "consolidation");
  if (!next && active) return { title: "Processing is in progress", detail: "Refresh status to inspect the latest available results.", actionLabel: "Refresh status", operation: "refresh" };
  if (!next) {
    for (const key of ["mapping", ...SHEET_OPERATIONS]) {
      const sheet = sheets.find(row => key === "mapping" ? row.mapping.code !== "saved" : !completedCodes.has(row[key].code));
      if (sheet) { next = candidate(sheet, key); break; }
    }
  }
  if (!next && sheets.length && !completedCodes.has(consolidation.code)) next = candidate(null, "consolidation");
  if (!next) return { title: inventoryComplete && sheets.length ? "Review Mark Complete readiness" : "Review source sheet inventory",
    detail: "Use the extraction tool to verify required checks before marking complete.", operation: "readiness", actionLabel: "View readiness checklist" };
  const { sheet, operation } = next;
  const titles = { mapping: `Review ${sheet?.name} mapping`, transform: `Transform ${sheet?.name}`,
    macros: `Run macros for ${sheet?.name}`, copy: `Copy unmapped columns for ${sheet?.name}`, consolidation: "Combine transformed sheets" };
  const details = { mapping: "Review and resolve the required headers, then retry its transformation.",
    transform: "Use the saved mapping to transform this sheet in the extraction tool.",
    macros: "Review the recorded result and run the required macros for this sheet.",
    copy: "Review unmapped columns and complete the copy operation for this sheet.",
    consolidation: "Combine the current source outputs after the required sheet operations finish." };
  return { title: titles[operation], detail: details[operation], sheetId: sheet?.id, operation,
    actionLabel: ({ mapping: "Review mapping", transform: "Review transformation", macros: "Review macros", copy: "Review unmapped columns", consolidation: "Review consolidation" })[operation] };
}

export function buildSovAnalyseSnapshot(raw = {}, document = {}) {
  const metadata = raw.metadata || {};
  const records = Array.isArray(metadata.sheetData) ? metadata.sheetData : [];
  const inventories = [metadata.origSheetNames, ...records.map(sheet => sheet?.origSheetNames)]
    .filter(names => Array.isArray(names) && names.length > 0);
  const sourceInventory = raw.sourceInventory?.sheetNames;
  if (!inventories.length && Array.isArray(sourceInventory) && sourceInventory.length) inventories.push(sourceInventory);
  const originals = inventories[0];
  const sourceNames = (originals || records.filter(sheet => sheet?.inputSheetName !== "Combined Sheet").map(sheet => sheet?.inputSheetName))
    .filter(name => typeof name === "string" && name.length > 0 && name !== "Combined Sheet");
  const names = [...new Set(sourceNames)];
  const additionalInventoryNames = inventories.flat().filter(name => typeof name === "string" && name && name !== "Combined Sheet" && !names.includes(name));
  names.push(...new Set(additionalInventoryNames));
  // Include observed records absent from a legacy inventory, but do not claim complete coverage.
  const extraNames = records.map(sheet => sheet?.inputSheetName)
    .filter(name => typeof name === "string" && name && name !== "Combined Sheet" && !names.includes(name));
  names.push(...new Set(extraNames));
  const warnings = [];
  if (raw.metadataMissing) warnings.push("No saved transformation metadata was found. Source sheets are shown with pending operations.");
  if (raw.statusMissing) warnings.push("Latest operation status is unavailable.");
  const inventoryComplete = Boolean(originals) && sourceNames.length === originals.length
    && extraNames.length === 0 && names.length === sourceNames.length
    && inventories.every(inventory => inventory.length === originals.length && inventory.every((name, index) => name === originals[index]));
  if (!inventoryComplete) warnings.push("The complete source sheet inventory is unverified; only known sheets are shown.");
  const sanitized = name => name.replace(/[\\/:*?"<>|]/g, "_").replace(/\s+/g, "_").toLowerCase();
  const hasCollision = names.some((name, index) => names.some((other, otherIndex) => otherIndex !== index && sanitized(name) === sanitized(other)))
    || names.some(name => records.filter(sheet => sheet?.inputSheetName === name).length > 1);
  if (hasCollision) warnings.push("Some sheet names share a legacy storage key. Their statuses require verification.");
  const combined = records.find(sheet => sheet?.inputSheetName === "Combined Sheet");
  const workflowOperations = latestWorkflowOperations(raw.workflow);
  const attempts = [];
  const savedStatuses = [];
  const sheets = names.map(name => {
    const matches = records.filter(sheet => sheet?.inputSheetName === name);
    const collision = matches.length > 1 || names.some(other => other !== name && sanitized(other) === sanitized(name));
    const sheet = matches.length === 1 ? matches[0] : {};
    const mappings = mappingsOf(sheet);
    const recordedOutputPath = sheet.transformationFilePath || sheet.transformedWorkbookPath;
    const normalizePath = value => typeof value === "string" ? value.replace(/^public\//, "") : "";
    const sourcePaths = [document.documentFullPath, document.extractedPdfExcelPath, metadata.extractedPdfExcelPath].map(normalizePath).filter(Boolean);
    const outputPath = sourcePaths.includes(normalizePath(recordedOutputPath)) ? null : recordedOutputPath;
    const unmapped = mappings.filter(item => String(item?.key || "").endsWith("_Unmapped"));
    const row = {
      id: JSON.stringify([document.document_id || "document", name]), name, collision,
      outputName: sheet.transformedSheetName || "", hasOutput: Boolean(outputPath),
      unmappedCount: unmapped.length || null,
      sourceRows: Number.isInteger(sheet.sourceRowCount) && sheet.sourceRowCount >= 0 ? sheet.sourceRowCount : null,
      mapping: state(mappings.length ? "saved" : "pending", mappings.length ? "Saved header mappings; required-header review must be verified in the extraction tool." : "Save the required header mappings in the extraction tool."),
      transform: state(outputPath ? "output" : "pending", outputPath ? "Saved transformed-output reference." : "No transformed output is recorded."),
      macros: state(normalizedStatus(sheet.macroStatusPanel) !== "unknown" ? normalizedStatus(sheet.macroStatusPanel) : sheet.runMacro === true ? "recorded" : "pending", "Based on saved macro status; output revision must be verified before Mark Complete."),
      copy: state(unmapped.length ? "partial" : "pending", unmapped.length ? `${unmapped.length} column mappings recorded; complete coverage is unverified.` : "No completed copy operation is recorded."),
      consolidation: state(Array.isArray(metadata.combinedSheets) ? metadata.combinedSheets.includes(name)
        ? metadata.isCombinedSheetUpdated === false ? "stale" : "included" : "excluded" : "excluded")
    };
    if (!collision) for (const target of ["mapping", ...SHEET_OPERATIONS]) {
      const value = row[target];
      if (["pending", "unknown"].includes(value.code)) continue;
      const code = value.code === "saved" || value.code === "output" ? "recorded" : value.code;
      savedStatuses.push({ id: `${row.id}:${target}:saved`, sheetId: row.id, sheet: name,
        operation: target, code, label: labels[code], saved: true, at: null,
        detail: "Saved sheet metadata; no execution timestamp or attempt record is available." });
    }
    const latest = raw.apiStatus?.[`fetch-mapped-data_${document.document_id}_${name}`];
    if (latest && typeof latest === "object") {
      const operation = operationOf(latest);
      const code = normalizedStatus(latest.apiStatus);
      const removed = operation === "copy" && String(latest.progress?.mappedDataAction || latest.mappedDataAction).toLowerCase() === "remove_unmapped_columns";
      if (operation === "macros" && normalizedStatus(sheet.macroStatusPanel) !== "unknown"
        && normalizedStatus(sheet.macroStatusPanel) !== code && code !== "unknown") {
        row.macros = state("unknown", "Saved macro status and latest API status disagree; review the extraction tool.");
      } else if (operation && code !== "unknown") {
        row[operation] = state(removed && code === "recorded" ? "pending" : operation === "copy" && code === "recorded" ? "copied" : code,
          removed && code === "recorded" ? "Copied unmapped columns were removed." : "Latest recorded attempt; output revision is unverified.");
      } else if (operation && latest.apiStatus) {
        row[operation] = state("unknown", "The latest operation status is not recognized. Refresh and verify it in the extraction tool.");
      }
      const attemptCode = removed && code === "recorded" ? "pending" : code;
      if (code !== "unknown" || (operation && latest.apiStatus)) attempts.push({ id: row.id, sheetId: row.id, sheet: name,
        operation: operation || "processing", code: attemptCode, label: labels[attemptCode], at: statusTime(latest),
        settled: removed && code === "recorded",
        detail: removed && code === "recorded" ? "Copied unmapped columns were removed. Copy is pending."
          : failedCodes.has(code) ? "The latest recorded attempt did not complete. Review the existing operation view for error details." : "Latest status only; complete attempt history is unavailable." });
    }
    const matchingWorkflowOperations = workflowOperations.filter(operation =>
      String(operation.inputSheetName || "").trim().toLowerCase() === name.trim().toLowerCase()
    );
    for (const operation of matchingWorkflowOperations) {
      const category = PROGRESS_STATUS[String(operation.status || "").trim().toUpperCase()];
      const target = workflowOperationTarget(operation);
      if (!SHEET_OPERATIONS.includes(target)) continue;
      // A queued plan has not executed and cannot invalidate an existing completed result.
      if (isUnattemptedWorkflowPlan(operation) && completedCodes.has(row[target].code)) continue;
      const code = category === "completed" ? target === "copy" ? "copied" : "recorded"
        : normalizedStatus(operation.status);
      const evidence = {
        id: operation.operationId || `${row.id}:${target}`,
        sheetId: row.id,
        sheet: name,
        operation: target,
        code,
        label: labels[code],
        workflow: true,
        settled: sovWorkflowTerminal(raw.workflow.status) && isUnattemptedWorkflowPlan(operation),
        at: workflowOperationTime(operation, raw.workflow),
        detail: `Current workflow operation: ${operation.status}.`
      };
      const apiEvidence = operationOf(latest) === target && latest?.apiStatus
        ? { ...row[target], at: statusTime(latest) } : null;
      const selectedEvidence = latestOperationEvidence(apiEvidence, evidence);
      row[target] = state(selectedEvidence.code, selectedEvidence.detail);
      attempts.push(evidence);
    }
    if (collision) {
      for (const key of ["mapping", "transform", "macros", "copy", "consolidation"]) row[key] = state("unknown", "Ambiguous legacy sheet identity.");
      row.hasOutput = false;
    }
    return row;
  });
  const combineStatus = raw.apiStatus?.[`combine-sheets_${document.document_id}`];
  let consolidation = combineStatus ? state(normalizedStatus(combineStatus.apiStatus) === "recorded" && metadata.isCombinedSheetUpdated === false
    ? "stale" : normalizedStatus(combineStatus.apiStatus))
    : state(combined?.transformationFilePath ? metadata.isCombinedSheetUpdated === false ? "stale" : "output" : "pending");
  if (combined?.transformationFilePath) savedStatuses.push({ id: "combine:saved", sheet: "Workbook",
    sheets: Array.isArray(metadata.combinedSheets) && metadata.combinedSheets.length ? metadata.combinedSheets : ["Workbook"],
    operation: "consolidation", code: metadata.isCombinedSheetUpdated === false ? "stale" : "recorded",
    label: metadata.isCombinedSheetUpdated === false ? labels.stale : labels.recorded,
    saved: true, at: null, detail: "Saved combined-output reference; no execution timestamp or attempt record is available." });
  if (combineStatus && typeof combineStatus === "object") {
    const code = normalizedStatus(combineStatus.apiStatus);
    if (code !== "unknown") attempts.push({ id: "combine", sheet: "Workbook", operation: "consolidation", code,
      sheets: Array.isArray(metadata.combinedSheets) && metadata.combinedSheets.length ? metadata.combinedSheets : ["Workbook"],
      label: labels[code], at: statusTime(combineStatus), detail: "Latest consolidation status; source revisions are unverified." });
  }
  const combineOperation = workflowOperations.find(operation => workflowOperationTarget(operation) === "consolidation");
  if (combineOperation && !(isUnattemptedWorkflowPlan(combineOperation) && completedCodes.has(consolidation.code))) {
    const code = normalizedStatus(combineOperation.status);
    const sources = (Array.isArray(raw.workflow.combineSources) ? raw.workflow.combineSources : [])
      .map(source => source?.inputSheetName).filter(Boolean);
    const evidence = { id: combineOperation.operationId || "workflow-combine", sheet: "Workbook",
      sheets: sources.length ? sources : Array.isArray(metadata.combinedSheets) && metadata.combinedSheets.length ? metadata.combinedSheets : ["Workbook"],
      operation: "consolidation", code, label: labels[code], workflow: true,
      settled: sovWorkflowTerminal(raw.workflow.status) && isUnattemptedWorkflowPlan(combineOperation),
      at: workflowOperationTime(combineOperation, raw.workflow), detail: `Current workflow operation: ${combineOperation.status}.` };
    const apiEvidence = combineStatus ? { ...consolidation, at: statusTime(combineStatus) } : null;
    const selectedEvidence = latestOperationEvidence(apiEvidence, evidence);
    consolidation = state(selectedEvidence.code === "recorded" && metadata.isCombinedSheetUpdated === false ? "stale" : selectedEvidence.code,
      selectedEvidence.detail);
    attempts.push(evidence);
  }
  attempts.sort((left, right) => (Date.parse(right.at) || 0) - (Date.parse(left.at) || 0));
  const outputs = sheets.filter(sheet => sheet.hasOutput).length;
  const operationEvidence = groupSovAnalyseHistory([...savedStatuses, ...attempts]);
  const history = operationEvidence.filter(group => HISTORY_OPERATIONS.has(group.operation));
  const active = operationEvidence.some(group => group.entries.some(attempt => !attempt.settled && ["running", "pending"].includes(attempt.code)))
    || Boolean(raw.workflow?.status && !sovWorkflowTerminal(raw.workflow.status));
  const recommendation = recommendAction(sheets, consolidation, active, inventoryComplete);
  const snapshot = { sheets, attempts, history, warnings, active, recommendation, fetchedAt: raw.fetchedAt,
    inventoryComplete, total: sheets.length, outputs,
    coverage: inventoryComplete && !hasCollision && sheets.length > 0 ? Math.floor(100 * outputs / sheets.length) : null,
    counts: { mapping: sheets.filter(sheet => sheet.mapping.code === "saved").length,
      transform: sheets.filter(sheet => sheet.hasOutput || completedCodes.has(sheet.transform.code)).length,
      macros: sheets.filter(sheet => completedCodes.has(sheet.macros.code)).length,
      copy: sheets.filter(sheet => completedCodes.has(sheet.copy.code)).length },
    readiness: buildReadiness(sheets, consolidation, inventoryComplete, operationEvidence), consolidation,
    businessStatus: document.processingStatus || "Unknown", workflow: raw.workflow || null };
  snapshot.progress = buildWorkflowProgress(raw.workflow, snapshot);
  return snapshot;
}

export function selectSovAnalyseRows(sheets, search, sort = "source") {
  const needle = String(search || "").trim().toLocaleLowerCase();
  const filtered = sheets.filter(sheet => `${sheet.name} ${sheet.outputName}`.toLocaleLowerCase().includes(needle));
  return sort === "name" ? [...filtered].sort((a, b) => a.name.localeCompare(b.name)) : filtered;
}

// One in-flight read and one timer; closing disposes subscriptions, not server work.
export function createSovAnalysePoller({ load, onData, onError, isActive, intervalMs = 10000,
  schedule = setTimeout, cancel = clearTimeout }) {
  let disposed = false;
  let inFlight = null;
  let timer;
  let failures = 0;
  let wasActive = false;
  function refresh() {
    if (disposed) return Promise.resolve();
    if (inFlight) return inFlight;
    if (timer !== undefined) cancel(timer);
    timer = undefined;
    inFlight = Promise.resolve().then(() => disposed ? undefined : load()).then(data => {
      if (disposed) return;
      failures = 0;
      wasActive = isActive(data);
      onData(data);
    }).catch(error => {
      if (disposed) return;
      failures += 1;
      onError(error);
    }).finally(() => {
      inFlight = null;
      if (!disposed && wasActive) timer = schedule(refresh, Math.min(60000, intervalMs * (2 ** Math.min(failures, 3))));
    });
    return inFlight;
  }
  return { refresh, dispose() { disposed = true; if (timer !== undefined) cancel(timer); } };
}

export function getSovWorkflowOperationProgress(operation, apiStatus, documentId) {
  const activeStatuses = ["RUNNING", "DISPATCHING", "RECOVERING", "RETRYING"];
  if (!operation || !activeStatuses.includes(String(operation.status || "").trim().toUpperCase())
    || typeof documentId !== "string" || !documentId
    || !apiStatus || typeof apiStatus !== "object" || Array.isArray(apiStatus)) return null;
  const target = workflowOperationTarget(operation);
  const attempt = Array.isArray(operation.attempts) ? operation.attempts.at(-1) : null;
  if (!target || typeof attempt?.attemptId !== "string" || !attempt.attemptId) return null;
  if (target !== "consolidation" && (typeof operation.inputSheetName !== "string" || !operation.inputSheetName)) return null;
  const key = target === "consolidation" ? `combine-sheets_${documentId}`
    : `fetch-mapped-data_${documentId}_${operation.inputSheetName}`;
  const record = apiStatus[key];
  const progress = record?.progress;
  if (!record || record.latestRequestId !== attempt.attemptId
    || !progress || typeof progress !== "object" || Array.isArray(progress)) return null;
  // Progress belongs to the current attempt, not an earlier operation on the same sheet.
  if ((record.mappedDataRunId && attempt.runId && record.mappedDataRunId !== attempt.runId)
    || (record.statePath && attempt.statePath && record.statePath !== attempt.statePath)
    || (record.mappedDataAction && workflowOperationTarget({ action: record.mappedDataAction }) !== target)
    || (target !== "consolidation" && typeof record.isRunMacro === "boolean" && record.isRunMacro !== (target === "macros"))) return null;
  const rawPercent = progress.percent;
  const numericPercent = typeof rawPercent === "number" ? rawPercent
    : typeof rawPercent === "string" && /^\d+(?:\.\d+)?$/.test(rawPercent.trim()) ? Number(rawPercent.trim()) : NaN;
  return {
    percent: Number.isFinite(numericPercent) && numericPercent >= 0 && numericPercent <= 100 ? numericPercent : null,
    phase: typeof progress.phase === "string" ? progress.phase : "",
    message: typeof progress.message === "string" ? progress.message : ""
  };
}
